import React, { useState } from 'react';
import PropTypes from 'prop-types';
import DownloadIcon from 'components/v2/icons/DownloadIcon';
import CargomaticInput from 'components/v2/input';
import LoadingCircle from 'components/ui/LoadingCircle';
import { Popover, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { sluggify } from 'utils/localization/utils';

const columnMapping = {
  SHIPMENT: 'Shipment',
  EQUIPMENT: 'Equipment',
  SHIPPER: 'Shipper',
  CARRIER: 'Carrier',
  ACCESSORIAL_TYPE: 'Accessorial Type',
  SHIPPER_COST: 'Shipper Cost',
  CARRIER_COST: 'Carrier Cost',
  REASON_CODE: 'Reason Code',
  PAYEE: 'Payee',
  ACTION_REQUIRED: 'Action Required',
  APPROVAL_DUE_DATE: 'Approval Due Date',
  ORIGIN_ADDRESS: 'Origin Address',
  DESTINATION_ADDRESS: 'Destination Address',
  DELIVER_AFTER: 'Deliver After',
  DELIVER_BY: 'Deliver By',
  SHIPPER_STATUS: 'Shipper Status',
  CARRIER_STATUS: 'Carrier Status',
  LAST_UPDATED: 'Last Updated',
};

const ExportAccessorialExceptionCSV = ({
  exportCSV,
  className,
  exportAccessorialsToCSV,
}) => {
  const { t } = useTranslation();
  const [selectedColumns, setSelectedColumns] = useState([
    ...Object.keys(columnMapping),
  ]);

  const handleColumnSelect = (key) => {
    const newSelectedColumns = [...selectedColumns];
    if (newSelectedColumns.includes(key)) {
      newSelectedColumns.splice(newSelectedColumns.indexOf(key), 1);
    } else {
      newSelectedColumns.push(key);
    }
    setSelectedColumns(newSelectedColumns);
  };

  const handleSelectAll = () => {
    if (selectedColumns.length === Object.keys(columnMapping).length) {
      return handleDeSelectAll();
    }
    setSelectedColumns(Object.keys(columnMapping));
  };

  const handleDeSelectAll = () => {
    setSelectedColumns([]);
  };

  const handleExport = () => {
    exportAccessorialsToCSV(selectedColumns);
  };

  const popperButtonClassNames = classNames({
    relative: true,
    button: true,
    'button-hollow-green': true,
    'hover:bg-white': !exportCSV,
  });

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <Popover.Button
            disabled={!exportCSV}
            className={popperButtonClassNames}
          >
            {exportCSV && (
              <div className="flex items-center space-x-1">
                <span>{t('EXPORT_CSV')}</span>
                <DownloadIcon />
              </div>
            )}
            {!exportCSV && (
              <LoadingCircle
                text={t('EXPORTING_CSV')}
                size={1}
                color="teal-500"
                textColor="teal-500"
              />
            )}
          </Popover.Button>
          <Popover.Overlay />
          <Transition
            show={open}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel
              static
              className="absolute left-0 z-10 h-auto w-44 px-4 mt-3 sm:px-0"
            >
              <div className="rounded-lg shadow-lg p-2 bg-white">
                <div className="dropdown-filter min-w-100 mb-2">
                  <div
                    key="Select All Columns Header"
                    className="checkbox-wrapper"
                  >
                    <CargomaticInput
                      id="Select All Columns"
                      key="Select All Columns"
                      label={t('SELECT_ALL_COLUMNS')}
                      type="checkbox"
                      checked={
                        selectedColumns.length ===
                        Object.keys(columnMapping).length
                      }
                      disabled={!exportCSV}
                      onChange={handleSelectAll}
                    />
                  </div>
                  <hr className="m-0 my-1" />
                  {Object.keys(columnMapping).map((key) => (
                    <div key={key} className="checkbox-wrapper">
                      <CargomaticInput
                        id={key}
                        key={key}
                        label={
                          key === 'SHIPMENT'
                            ? titleize(t(key, { count: 1 }))
                            : titleize(t(sluggify(columnMapping[key])))
                        }
                        type="checkbox"
                        checked={selectedColumns.includes(key)}
                        disabled={!exportCSV}
                        onChange={() => handleColumnSelect(key)}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex justify-start items-center gap-x-2">
                  <button
                    className="button button-green text-xs p-2"
                    onClick={() => {
                      handleExport();
                      close();
                    }}
                    disabled={!exportCSV}
                  >
                    {titleize(t('DOWNLOAD'))}
                  </button>
                  <button
                    className="button button-red text-xs p-2"
                    onClick={handleDeSelectAll}
                    disabled={!exportCSV}
                  >
                    {t('CLEAR_ALL')}
                  </button>
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

ExportAccessorialExceptionCSV.propTypes = {
  exportCSV: PropTypes.bool.isRequired,
  className: PropTypes.string.isRequired,
  exportAccessorialsToCSV: PropTypes.func.isRequired,
};

export default ExportAccessorialExceptionCSV;
